/**
 * Alias redirect helper (Task #1730 — Tech Debt M).
 *
 * Extracted from `App.tsx` so the redirect registry in
 * `client/src/routes/legacyRedirects.tsx` can import it without pulling in
 * the entire route tree. Behaviour is unchanged from the original:
 *
 *   - Wraps a wouter `<Redirect>` with a one-shot `app.alias_redirect`
 *     telemetry emit so the SA usage dashboard can plot legacy-URL
 *     traffic per alias and decide when each alias is safe to retire
 *     (sunset criteria documented in
 *     `docs/source-of-truth/runtime/ptw-alias-sunset-schedule.md`).
 *   - The emit fires inside a `useEffect`, so it runs once per redirect
 *     mount (i.e. once per landing on the alias URL).
 *   - Properties are intentionally coarse — `from` and `to` are URL
 *     templates, never query strings, so the event keeps within the
 *     no-PII telemetry contract.
 */
import { useEffect } from "react";
import { Redirect } from "wouter";
import { trackEvent } from "@/lib/telemetry";

export function AliasRedirect({ from, to }: { from: string; to: string }) {
  useEffect(() => {
    trackEvent("app.alias_redirect", { from, to }, { surface: from });
  }, [from, to]);
  return <Redirect to={to} />;
}

/**
 * `/equipment/ptw-panel` alias router — Task #1355 (PTW CAP-6).
 *
 * The standalone PTW Equipment Panel is now part of the unified Permit
 * Detail page (Equipment panel). Two redirect branches:
 *
 *   1. `?permitId=X` present  → `/ptw/X?panel=equipment&edit=1`
 *      Deep-links straight into the permit's Equipment panel in edit
 *      mode so users can keep their existing flow (attach + validate
 *      readiness for a specific permit).
 *   2. No permitId            → `/ptw?tab=all-permits&filter=equipment-blocked`
 *      Falls back to the Console All Permits filter (Phase 5.A behaviour)
 *      so users picking a permit out of the equipment-blocked list still
 *      land somewhere actionable.
 *
 * Both branches go through `<AliasRedirect>` so the canonical
 * `app.alias_redirect` telemetry fires once per landing.
 */
/**
 * `/super-admin/partner-commercial` → `/super-admin/commercial` redirect.
 *
 * Task #1672 Round-6 consolidated PartnerPlans / PartnerSubscriptions /
 * PartnerRevenue / SubscriptionTiers / PartnerCommercial into
 * `CommercialWorkspace`. This redirect preserves any `?tab=` query param
 * and translates old tab names into the CommercialWorkspace coordinate
 * system (e.g. `plans` → `partner-plans`).
 */
export function PartnerCommercialRedirect() {
  const search = typeof window !== "undefined" ? window.location.search : "";
  const sp = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const oldTab = sp.get("tab");
  const tabMap: Record<string, string> = {
    plans: "partner-plans",
    subscriptions: "partner-plans",
    revenue: "revenue",
  };
  const mappedTab = oldTab ? tabMap[oldTab] ?? oldTab : "partner-plans";
  return (
    <AliasRedirect
      from="/super-admin/partner-commercial"
      to={`/super-admin/commercial?tab=${mappedTab}`}
    />
  );
}

export function PtwEquipmentPanelAlias() {
  const search = typeof window !== "undefined" ? window.location.search : "";
  const sp = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const permitId = sp.get("permitId");
  if (permitId && permitId.trim() !== "") {
    const to = `/ptw/${encodeURIComponent(permitId)}?panel=equipment&edit=1`;
    return <AliasRedirect from="/equipment/ptw-panel" to={to} />;
  }
  return (
    <AliasRedirect
      from="/equipment/ptw-panel"
      to="/ptw/all-permits?filter=equipment-blocked"
    />
  );
}
